import React from 'react';
import { 
  FaUniversity, FaHeart, FaRocket, FaHandsHelping, FaQuoteLeft, 
  FaCalendarAlt, FaAward, FaUsers, FaLightbulb, FaChartLine, 
  FaBookOpen, FaLaptop, FaGlobe, FaCheckCircle, FaStar,
  FaGraduationCap, FaChalkboardTeacher, FaArrowRight, FaBuilding,
  FaTree, FaWifi, FaBus, FaUtensils, FaShieldAlt, FaMicrophone,
  FaCalendar, FaFlagCheckered
} from 'react-icons/fa';
import { GiTeacher, GiBrain, GiBookshelf, GiMicroscope, GiCricketBat } from 'react-icons/gi';

const AboutUs = () => {
  // Core Values – restored original colors
  const values = [
    { icon: FaHeart, title: "Integrity", desc: "Upholding ethical values in all endeavors", color: "text-red-500", bg: "bg-red-50" },
    { icon: FaRocket, title: "Excellence", desc: "Striving for highest standards", color: "text-blue-600", bg: "bg-blue-50" },
    { icon: FaLightbulb, title: "Innovation", desc: "Embracing new ideas and approaches", color: "text-yellow-500", bg: "bg-yellow-50" },
    { icon: FaHandsHelping, title: "Inclusivity", desc: "Welcoming diversity and equal opportunities", color: "text-green-500", bg: "bg-green-50" }
  ];

  // Statistics
  const stats = [
    { number: "1965", label: "Established", icon: FaCalendarAlt, desc: "Over 5 decades of excellence" },
    { number: "5000+", label: "Alumni Worldwide", icon: FaUsers, desc: "Global network of achievers" },
    { number: "80+", label: "Expert Faculty", icon: GiTeacher, desc: "Highly qualified educators" },
    { number: "50+", label: "Awards", icon: FaAward, desc: "National & state level honors" },
    { number: "95%", label: "Placement Rate", icon: FaChartLine, desc: "Consistent career success" },
    { number: "1500+", label: "Active Students", icon: FaGraduationCap, desc: "Vibrant student community" }
  ];

  // Milestones – data for the horizontal timeline
  const milestones = [
    { 
      year: "1965", 
      title: "Foundation", 
      desc: "Established with 100 students in a modest building, marking the beginning of a legacy.", 
      icon: FaCalendarAlt,
      color: "from-blue-600 to-cyan-500"
    },
    { 
      year: "1980", 
      title: "First Graduate Batch", 
      desc: "50 students passed out with university ranks, setting a tradition of academic excellence.", 
      icon: FaGraduationCap,
      color: "from-green-500 to-emerald-500"
    },
    { 
      year: "2000", 
      title: "New Campus", 
      desc: "Shifted to current 10-acre campus with modern facilities, spacious classrooms, and labs.", 
      icon: FaBuilding,
      color: "from-blue-300 to-blue-400"
    },
    { 
      year: "2010", 
      title: "NAAC Accreditation", 
      desc: "Achieved 'B+' grade with high scores, validating our commitment to quality education.", 
      icon: FaAward,
      color: "from-orange-500 to-red-500"
    },
    { 
      year: "2020", 
      title: "Digital Campus", 
      desc: "Introduced smart classrooms, high-speed Wi-Fi, and e-library to embrace digital learning.", 
      icon: FaLaptop,
      color: "from-teal-500 to-cyan-500"
    },
    { 
      year: "2024", 
      title: "A+ Grade", 
      desc: "NAAC A+ accreditation with CGPA 3.5, reaffirming our position among top colleges.", 
      icon: FaFlagCheckered,
      color: "from-indigo-500 to-blue-600"
    }
  ];

  // Why Choose Us
  const reasons = [
    { icon: FaChalkboardTeacher, title: "Quality Education", desc: "Experienced faculty & modern curriculum" },
    { icon: FaLaptop, title: "Smart Campus", desc: "Digital classrooms & high-speed Wi-Fi" },
    { icon: GiBookshelf, title: "Rich Library", desc: "10,000+ books, journals & e-resources" },
    { icon: GiMicroscope, title: "Advanced Labs", desc: "Well-equipped science & computer labs" },
    { icon: FaGlobe, title: "Global Exposure", desc: "Seminars, workshops & guest lectures" },
    { icon: FaCheckCircle, title: "Holistic Development", desc: "Sports, cultural & extracurricular activities" },
    { icon: FaBuilding, title: "Modern Infrastructure", desc: "Spacious classrooms, auditorium, seminar halls" },
    { icon: FaTree, title: "Green Campus", desc: "Eco-friendly environment with lush gardens" }
  ];

  // Infrastructure highlights
  const infrastructure = [
    { icon: FaBookOpen, title: "Library", desc: "Digital library with 10,000+ volumes" },
    { icon: FaLaptop, title: "Computer Centre", desc: "100+ computers with high-speed internet" },
    { icon: GiMicroscope, title: "Science Labs", desc: "Physics, Chemistry, Biology labs" },
    { icon: GiCricketBat, title: "Sports Complex", desc: "Cricket, basketball, volleyball courts" },
    { icon: FaBus, title: "Transport", desc: "College bus service on all routes" },
    { icon: FaUtensils, title: "Canteen", desc: "Healthy and hygienic food" },
    { icon: FaWifi, title: "Wi-Fi Campus", desc: "24/7 internet connectivity" },
    { icon: FaMicrophone, title: "Auditorium", desc: "500-seat AC auditorium for events" }
  ];

  // Scroll handling for timeline
  const scrollLeft = () => {
    const container = document.getElementById('timeline-scroll');
    if (container) container.scrollBy({ left: -400, behavior: 'smooth' });
  };
  const scrollRight = () => {
    const container = document.getElementById('timeline-scroll');
    if (container) container.scrollBy({ left: 400, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50/20 to-white">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-blue-800 via-indigo-700 to-purple-800 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About Us</h1>
          <div className="w-24 h-1 bg-yellow-400 mx-auto rounded-full mb-6"></div>
          <p className="text-xl max-w-3xl mx-auto">Know more about our legacy, mission, and commitment to excellence in education since 1965</p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16">
        {/* History Section */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-600 px-4 py-1.5 rounded-full mb-4">
              <FaUniversity className="text-white" />
              <span className="text-white font-semibold">Our Legacy</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Excellence in Education
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Since 1965</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              R.D.S.Snatkottar Mahavidyalaya was established in 1965 by visionary educationists with a mission to provide quality higher education to the youth of rural Uttar Pradesh. Over the past five decades, we have grown into a premier institution affiliated with [University Name], recognized for academic excellence, holistic development, and strong value-based education.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Our journey has been marked by continuous innovation in teaching methodologies, infrastructure development, and creating an environment that nurtures talent. We take pride in our alumni who have excelled in various fields – from civil services to corporate leadership, from research to entrepreneurship.
            </p>
            <p className="text-gray-600 leading-relaxed">
              With a strong commitment to empowering students, we have consistently produced top rankers in university examinations and have been recognized for our contributions to higher education. Our state-of-the-art facilities and dedicated faculty ensure that every student receives the best possible education.
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-lg">
            <FaQuoteLeft className="text-4xl text-blue-600 mb-4" />
            <p className="text-gray-700 italic leading-relaxed text-lg">
              "Education is not just about acquiring knowledge; it's about shaping character, building values, and preparing individuals to face the challenges of life with confidence and integrity."
            </p>
            <p className="text-right font-semibold text-gray-800 mt-4">- Founder's Message</p>
          </div>
        </div>

        {/* Horizontal Timeline - Our Journey Through Time */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-800">Our Journey Through Time</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-500 mx-auto rounded-full mt-3"></div>
            <p className="text-gray-600 mt-2">Milestones that shaped our institution</p>
          </div>

          <div className="relative">
            {/* Scroll Buttons */}
            <button 
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all duration-300 border border-gray-200"
            >
              ←
            </button>
            <button 
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all duration-300 border border-gray-200"
            >
              →
            </button>

            {/* Scrollable Container */}
            <div 
              id="timeline-scroll"
              className="flex overflow-x-auto gap-6 pb-6 scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {milestones.map((milestone, idx) => (
                <div key={idx} className="flex-none w-80 group">
                  <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
                    {/* Colored top bar */}
                    <div className={`h-1.5 w-full bg-gradient-to-r ${milestone.color}`}></div>
                    
                    <div className="p-6 text-center">
                      {/* Year badge */}
                      <div className="inline-block bg-blue-600 text-white font-bold text-lg px-4 py-1 rounded-full mb-4">
                        {milestone.year}
                      </div>
                      
                      {/* Icon */}
                      <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${milestone.color} rounded-xl flex items-center justify-center shadow-md`}>
                        <milestone.icon className="text-white text-2xl" />
                      </div>
                      
                      <h4 className="text-xl font-bold text-gray-800 mb-2">{milestone.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{milestone.desc}</p>
                    </div>
                    
                    {/* Hover gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
              <FaRocket className="text-white text-2xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Our Vision</h3>
            <p className="text-gray-600 leading-relaxed">
              To be a center of excellence in higher education, nurturing individuals who are intellectually 
              competent, morally upright, and socially committed to contribute meaningfully to society.
            </p>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
              <GiBrain className="text-indigo-600 text-2xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Our Mission</h3>
            <p className="text-gray-600 leading-relaxed">
              To provide quality education that fosters intellectual growth, promotes research and innovation, 
              instills ethical values, and prepares students for leadership roles in their chosen fields.
            </p>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-800">Why Choose RDS?</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-500 mx-auto rounded-full mt-3"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasons.map((reason, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <reason.icon className="text-white text-xl" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800 mb-1">{reason.title}</h4>
                  <p className="text-gray-600 text-sm">{reason.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Infrastructure Highlights */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-800">Campus Infrastructure</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-500 mx-auto rounded-full mt-3"></div>
            <p className="text-gray-600 mt-2">Modern facilities for a conducive learning environment</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {infrastructure.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-5 text-center shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
                <item.icon className="text-3xl text-blue-600 mx-auto mb-3" />
                <h4 className="text-lg font-bold text-gray-800 mb-1">{item.title}</h4>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-800">Our Core Values</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-500 mx-auto rounded-full mt-3"></div>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <div key={idx} className={`${value.bg} rounded-2xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1`}>
                <div className={`w-14 h-14 ${value.bg} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <value.icon className={`text-2xl ${value.color}`} />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">{value.title}</h4>
                <p className="text-gray-600 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA – Buttons with white text & icons */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-center text-white shadow-xl">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">Join Our Legacy of Excellence</h3>
          <p className="text-white mb-6 max-w-2xl mx-auto">
            Be a part of an institution that has been shaping futures for over 50 years. Your journey to success starts here.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            {/* Apply Now button – white text & icon */}
            <button className="border-2 border-white text-white px-6 py-2 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition flex items-center gap-2">
              Apply Now <FaArrowRight />
            </button>
            {/* Download Prospectus button – white text */}
            <button className="border-2 border-white text-white px-6 py-2 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition">
              Download Prospectus
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default AboutUs;